/**
 * Utility functions for consistent date formatting across the application
 * All dates are displayed in the store's configured timezone (from settings)
 */

/**
 * Resolve the store timezone: explicit override → saved setting → default.
 * Reads localStorage synchronously (kept in sync with the DB by useSettings).
 * @param {string|null} timezone - Optional explicit override
 * @returns {string} IANA timezone id
 */
const resolveTimezone = (timezone) => {
  if (timezone) return timezone;
  try {
    const settings = JSON.parse(localStorage.getItem('twigs_settings'));
    return settings?.timezone || 'America/New_York';
  } catch (error) {
    return 'America/New_York';
  }
};

/**
 * The store's configured timezone id, for callers that need to pass it to
 * their own Intl/toLocale* calls.
 * @param {string|null} timezone - Optional explicit override
 * @returns {string} IANA timezone id
 */
export const getStoreTimezone = (timezone = null) => resolveTimezone(timezone);

/**
 * Formats a date to display in the store's configured timezone with day of week in bold
 * Format: "Wednesday 9/17/2025"
 * @param {Date|string} date - The date to format
 * @param {string} timezone - Optional timezone override
 * @returns {Object} - Object with formatted date and JSX element for display
 */
export const formatDateWithDay = (date, timezone = null) => {
  if (!date) return { text: 'Not specified', element: 'Not specified' };

  // Get timezone from settings if not provided
  let targetTimezone = timezone;
  if (!targetTimezone) {
    try {
      const settings = JSON.parse(localStorage.getItem('twigs_settings'));
      targetTimezone = settings?.timezone || 'America/New_York';
    } catch (error) {
      targetTimezone = 'America/New_York'; // fallback
    }
  }

  try {
    let dateObj;
    let isLocalDate = false;

    // If it's a string in YYYY-MM-DD format, parse it as local date to avoid timezone issues
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      const [year, month, day] = date.split('-').map(Number);
      dateObj = new Date(year, month - 1, day); // month is 0-indexed
      isLocalDate = true; // Mark this as a local date (no timezone conversion needed)
    } else {
      dateObj = new Date(date);
    }

    // Format date
    const options = {
      weekday: 'long',
      month: 'numeric',
      day: 'numeric',
      year: 'numeric'
    };

    // Only apply timezone if it's NOT a local date (YYYY-MM-DD)
    if (!isLocalDate) {
      options.timeZone = targetTimezone;
    }

    const formatted = dateObj.toLocaleDateString('en-US', options);
    const parts = formatted.split(' ');
    const dayOfWeek = parts[0].replace(',', ''); // Remove comma after day
    const dateOnly = parts.slice(1).join(' ');

    return {
      text: `${dayOfWeek} ${dateOnly}`,
      element: (
        <>
          <strong>{dayOfWeek}</strong> {dateOnly}
        </>
      )
    };
  } catch (error) {
    console.error('Date formatting error:', error);
    return { text: date, element: date };
  }
};

/**
 * Formats a date for delivery information
 * @param {Date|string} date 
 * @returns {Object}
 */
export const formatDeliveryDate = (date) => {
  return formatDateWithDay(date);
};

/**
 * Formats a date for order creation
 * @param {Date|string} date 
 * @returns {Object}
 */
export const formatOrderDate = (date) => {
  return formatDateWithDay(date);
};

/**
 * Formats date and time together in the store's configured timezone
 * @param {Date|string} date
 * @param {string} timezone - Optional timezone override
 * @returns {string}
 */
export const formatDateTimeInTimezone = (date, timezone = null) => {
  if (!date) return 'Not specified';

  // Get timezone from settings if not provided
  let targetTimezone = timezone;
  if (!targetTimezone) {
    try {
      const settings = JSON.parse(localStorage.getItem('twigs_settings'));
      targetTimezone = settings?.timezone || 'America/New_York';
    } catch (error) {
      targetTimezone = 'America/New_York'; // fallback
    }
  }

  try {
    const dateObj = new Date(date);
    return dateObj.toLocaleString('en-US', {
      timeZone: targetTimezone,
      weekday: 'long',
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  } catch (error) {
    console.error('DateTime formatting error:', error);
    return date;
  }
};

/**
 * @deprecated Use formatDateTimeInTimezone() instead
 * Formats date and time together in Eastern timezone
 * @param {Date|string} date
 * @returns {string}
 */
export const formatDateTimeEastern = (date) => {
  return formatDateTimeInTimezone(date, 'America/New_York');
};

/**
 * Gets the current time in the specified timezone as an ISO string
 * This ensures new orders are created with the correct store timezone
 * @param {string} timezone - The timezone to use (defaults to store timezone from settings)
 * @returns {string} ISO string adjusted for the specified timezone
 */
export const getCurrentTimeInTimezone = (timezone = null) => {
  // Get timezone from settings if not provided
  let targetTimezone = timezone;
  if (!targetTimezone) {
    try {
      const settings = JSON.parse(localStorage.getItem('twigs_settings'));
      targetTimezone = settings?.timezone || 'America/New_York';
    } catch (error) {
      targetTimezone = 'America/New_York'; // fallback
    }
  }

  // Get current time
  const now = new Date();

  // Get the offset for target timezone (accounting for DST)
  const targetTime = new Date(now.toLocaleString("en-US", {timeZone: targetTimezone}));
  const utcTime = new Date(now.toLocaleString("en-US", {timeZone: "UTC"}));
  const offset = targetTime.getTime() - utcTime.getTime();

  // Adjust the current time by the offset
  const adjustedTime = new Date(now.getTime() + offset);
  return adjustedTime.toISOString();
};

/**
 * @deprecated Use getCurrentTimeInTimezone() instead
 * Gets the current time in Eastern timezone as an ISO string
 * @returns {string} ISO string adjusted for Eastern time
 */
export const getCurrentEasternTime = () => {
  return getCurrentTimeInTimezone('America/New_York');
};

/**
 * Compact relative formatting for delivery timestamps.
 * Returns "3:42 PM • Today" / "3:42 PM • Yesterday" / "3:42 PM • MMM D".
 * @param {Date|string|null} date
 * @param {Date} [now=new Date()] - injectable for tests
 * @param {string} [timezone] - optional override; otherwise from settings
 * @returns {string}
 */
export const formatDeliveryTimeRelative = (date, now = new Date(), timezone = null) => {
  if (!date) return '';

  let targetTimezone = timezone;
  if (!targetTimezone) {
    try {
      const settings = JSON.parse(localStorage.getItem('twigs_settings'));
      targetTimezone = settings?.timezone || 'America/New_York';
    } catch (error) {
      targetTimezone = 'America/New_York';
    }
  }

  try {
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return '';

    const time = dateObj.toLocaleString('en-US', {
      timeZone: targetTimezone,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });

    // Compare calendar day in target timezone (YYYY-MM-DD strings).
    const dayKey = (d) => d.toLocaleString('en-CA', {
      timeZone: targetTimezone,
      year: 'numeric', month: '2-digit', day: '2-digit',
    });
    const target = dayKey(dateObj);
    const today = dayKey(now);

    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const yKey = dayKey(yesterday);

    let label;
    if (target === today) label = 'Today';
    else if (target === yKey) label = 'Yesterday';
    else label = dateObj.toLocaleString('en-US', {
      timeZone: targetTimezone, month: 'short', day: 'numeric',
    });

    return `${time} • ${label}`;
  } catch (error) {
    console.error('formatDeliveryTimeRelative error:', error);
    return '';
  }
};

/**
 * The current calendar day in the store timezone as 'YYYY-MM-DD'.
 * Use this instead of `new Date().toISOString().split('T')[0]` (which is UTC)
 * for "today" defaults and comparisons so the day flips at store midnight.
 * @param {string|null} timezone - Optional override
 * @returns {string}
 */
export const getTodayInTimezone = (timezone = null) => {
  const tz = resolveTimezone(timezone);
  return new Date().toLocaleDateString('en-CA', { timeZone: tz });
};

/** The current wall-clock time as 'HH:MM' (24h) in the store timezone — for cutoffs. */
export const getNowHHMMInTimezone = (timezone = null) => {
  const tz = resolveTimezone(timezone);
  return new Date().toLocaleTimeString('en-GB', { timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false });
};

/**
 * The current hour (0-23) in the store timezone — e.g. for time-of-day
 * greetings — instead of the browser-local `new Date().getHours()`.
 * @param {string|null} timezone - Optional override
 * @returns {number}
 */
export const getCurrentHourInTimezone = (timezone = null) => {
  const tz = resolveTimezone(timezone);
  const hour = new Date().toLocaleString('en-US', { timeZone: tz, hour: 'numeric', hourCycle: 'h23' });
  return parseInt(hour, 10) || 0;
};

/**
 * Time-only formatting (e.g. "3:42 PM") in the store timezone.
 * @param {Date|string} date
 * @param {string|null} timezone - Optional override
 * @returns {string}
 */
export const formatTimeInTimezone = (date, timezone = null) => {
  if (!date) return '';
  const tz = resolveTimezone(timezone);
  try {
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return '';
    return dateObj.toLocaleTimeString('en-US', {
      timeZone: tz,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch (error) {
    return '';
  }
};

/**
 * Format a date with caller-supplied Intl options in the store timezone.
 * Date-only strings (YYYY-MM-DD) are parsed as a local calendar date so the
 * displayed day never shifts; full timestamps are converted into the store
 * timezone. Lets callers keep a custom format (e.g. long month) while staying
 * timezone-correct.
 * @param {Date|string} date
 * @param {Object} options - Intl.DateTimeFormat options
 * @param {string|null} timezone - Optional override
 * @returns {string}
 */
export const formatDateInTimezone = (date, options = {}, timezone = null) => {
  if (!date) return '';
  const tz = resolveTimezone(timezone);
  try {
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      const [year, month, day] = date.split('-').map(Number);
      return new Date(year, month - 1, day).toLocaleDateString('en-US', options);
    }
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return typeof date === 'string' ? date : '';
    return dateObj.toLocaleDateString('en-US', { ...options, timeZone: tz });
  } catch (error) {
    return typeof date === 'string' ? date : '';
  }
};

/**
 * The calendar day of `date` in the store timezone as 'YYYY-MM-DD'. Use for
 * values that back a date input, filter, or are saved as a calendar day (so
 * they don't drift to the UTC day). A bare 'YYYY-MM-DD' is passed through.
 * @param {Date|string} date
 * @param {string|null} timezone - Optional override
 * @returns {string}
 */
export const toDateKeyInTimezone = (date, timezone = null) => {
  if (!date) return '';
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) return date;
  const tz = resolveTimezone(timezone);
  try {
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-CA', { timeZone: tz });
  } catch (error) {
    return '';
  }
};

/**
 * Format an instant as a `<input type="datetime-local">` value
 * ('YYYY-MM-DDTHH:mm') showing the store-timezone wall clock — so the field
 * displays store-local time rather than the browser's. Pair with
 * datetimeLocalToISO() on save.
 * @param {Date|string} [date=new Date()]
 * @param {string|null} timezone - Optional override
 * @returns {string}
 */
export const toDatetimeLocalInTimezone = (date = new Date(), timezone = null) => {
  const tz = resolveTimezone(timezone);
  try {
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return '';
    const datePart = d.toLocaleDateString('en-CA', { timeZone: tz });
    const timePart = d.toLocaleTimeString('en-GB', {
      timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false,
    });
    return `${datePart}T${timePart}`;
  } catch (error) {
    return '';
  }
};

/**
 * Convert a `datetime-local` value ('YYYY-MM-DDTHH:mm'), interpreted as a
 * wall clock in the store timezone, into a UTC ISO instant. The browser's
 * `new Date(localValue)` would interpret it in the browser's timezone — this
 * pins it to the store's instead. Inverse of toDatetimeLocalInTimezone().
 * @param {string} localValue
 * @param {string|null} timezone - Optional override
 * @returns {string} ISO string
 */
export const datetimeLocalToISO = (localValue, timezone = null) => {
  if (!localValue) return '';
  const tz = resolveTimezone(timezone);
  try {
    const [datePart, timePart = '00:00'] = localValue.split('T');
    const [y, mo, d] = datePart.split('-').map(Number);
    const [h, mi] = timePart.split(':').map(Number);
    // Treat the entered wall clock as UTC, then correct by the tz offset at
    // that instant (same offset approach as getCurrentTimeInTimezone).
    const utcGuess = Date.UTC(y, mo - 1, d, h, mi);
    const asUtc = new Date(new Date(utcGuess).toLocaleString('en-US', { timeZone: 'UTC' }));
    const asTz = new Date(new Date(utcGuess).toLocaleString('en-US', { timeZone: tz }));
    const offset = asTz.getTime() - asUtc.getTime();
    return new Date(utcGuess - offset).toISOString();
  } catch (error) {
    return '';
  }
};